import { useCallback, useEffect, useState } from "react";
import { CalendarDays, Inbox, PowerOff, Upload, WifiOff } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useI18n } from "@/context/I18nContext";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter,
    AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { CostUploadDialog, CoverageView } from "./CostUploadDialog";
import { ItemDialog } from "./ItemDialog";
import { orderedGroups, useScItems } from "./ItemsAdmin";
import { SC_TIMEOUT_MS, fmt, fmtDateTime, fmtPeriod, scMessage } from "./scUtils";

const K = OPS.sc.costs;
const who = (b) => (b && typeof b === "object" ? b.name : b) || "";

function SetRow({ s, onDeactivate }) {
    const { t, lang } = useI18n();
    const n = (v) => new Intl.NumberFormat(lang === "ar" ? "ar-SA" : "en-US").format(v || 0);
    const files = (s.files || []).map((f) => f.name).filter(Boolean);
    return (
        <li data-testid={K.set(s.id)} data-active={s.is_active ? "true" : "false"}
            className={`space-y-1 py-3 ${s.is_active ? "" : "opacity-60"}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="flex items-center gap-2 text-sm font-bold text-[color:var(--ink)]">
                    {fmtPeriod(s.period, lang)}
                    <Badge variant={s.is_active ? "success" : "secondary"} className="px-1.5 py-0 text-[10px]">
                        {t(s.is_active ? "stk_admin_active" : s.superseded_by ? "stk_costs_superseded" : "stk_admin_inactive")}
                    </Badge>
                    <Badge variant={s.source === "seed" ? "brown" : "outline"} className="px-1.5 py-0 text-[10px]">
                        {t(s.source === "seed" ? "stk_costs_source_seed" : "stk_costs_source_upload")}
                    </Badge>
                </span>
                {s.is_active ? (
                    <Button data-testid={K.deactivate(s.id)} variant="ghost" size="sm" onClick={() => onDeactivate(s)}
                            className="h-8 gap-1 px-2 text-[11px] text-[color:var(--danger-soft-fg)]">
                        <PowerOff className="h-3.5 w-3.5" />{t("stk_costs_deactivate")}
                    </Button>
                ) : null}
            </div>
            <p className="text-[11px] text-[color:var(--ink-muted)]">
                {t("stk_costs_as_of")} <bdi dir="ltr">{s.as_of}</bdi>
                {s.date_from ? <> · <bdi dir="ltr">{s.date_from} – {s.date_to}</bdi></> : null}
                {" · "}{fmt(t("stk_costs_file_items"), { n: n(s.items_n) })}
                {s.rows ? <> · {fmt(t("stk_costs_file_rows"), { n: n(s.rows) })}</> : null}
            </p>
            {files.length ? (
                <p className="truncate text-[11px] text-[color:var(--ink-faint)]" dir="ltr">{files.join(" · ")}</p>
            ) : null}
            <p className="text-[11px] text-[color:var(--ink-faint)]">
                {who(s.created_by) ? <>{who(s.created_by)} · </> : null}{fmtDateTime(s.created_at, lang)}
                {s.note ? <> · {s.note}</> : null}
            </p>
        </li>
    );
}

/** «التكاليف» (finance): the uploaded ERP cost sets, the upload dialog, and the
 *  coverage of a chosen month — items without a cost open the item dialog
 *  (manual cost / ERP code / cost unit). */
export function CostsAdmin({ scope }) {
    const { t, lang } = useI18n();
    const [sets, setSets] = useState(null);
    const [failed, setFailed] = useState(false);
    const [reloadKey, setReloadKey] = useState(0);
    const [uploadOpen, setUploadOpen] = useState(false);
    const [confirmSet, setConfirmSet] = useState(null);
    const [busy, setBusy] = useState(false);
    const periods = scope?.periods || [];
    const [period, setPeriod] = useState(scope?.default_period || periods[0] || "");
    const [coverage, setCoverage] = useState(null);
    const [covLoading, setCovLoading] = useState(false);
    const { items, load: loadItems } = useScItems();
    const [editing, setEditing] = useState(null);

    const reload = () => setReloadKey((k) => k + 1);

    useEffect(() => {
        let alive = true;
        setFailed(false);
        api.get("/sc/costs/sets")
            .then((r) => { if (alive) setSets(r.data?.sets || []); })
            .catch((e) => {
                if (!alive) return;
                // the list would otherwise spin forever: always offer the retry box
                const msg = scMessage(e, lang);
                if (msg) toast.error(msg);
                setFailed(true);
            });
        return () => { alive = false; };
    }, [reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

    const loadCoverage = useCallback(() => {
        if (!period) return undefined;
        let alive = true;
        setCovLoading(true);
        api.get("/sc/costs/coverage", { params: { period } })
            .then((r) => { if (alive) setCoverage(r.data); })
            .catch((e) => {
                if (!alive) return;
                // never leave another month's coverage under the newly chosen month
                setCoverage((c) => (c && c.period !== period ? null : c));
                toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
            })
            .finally(() => { if (alive) setCovLoading(false); });
        return () => { alive = false; };
    }, [period, lang, t]);
    useEffect(() => loadCoverage(), [loadCoverage, reloadKey]);

    const deactivate = async () => {
        if (!confirmSet) return;
        setBusy(true);
        try {
            await api.post(`/sc/costs/sets/${confirmSet.id}/deactivate`, {}, { timeout: SC_TIMEOUT_MS });
            toast.success(t("stk_costs_deactivated"));
            setConfirmSet(null);
            reload();
        } catch (e) {
            toast.error(scMessage(e, lang) || t("stk_admin_offline_error"));
        } finally {
            setBusy(false);
        }
    };

    // coverage rows carry a few fields only; the dialog needs the full item
    const openItem = async (row) => {
        const list = items.length ? items : await loadItems();
        const item = (list || []).find((i) => i.id === row.id);
        if (item) setEditing(item); else toast.error(t("stk_costs_item_missing"));
    };

    return (
        <div data-testid={K.view} className="space-y-3">
            <section className="rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <header className="flex flex-wrap items-center justify-between gap-2 pb-1">
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("stk_costs_sets_title")}</h3>
                        <p className="text-[11px] text-[color:var(--ink-muted)]">{t("stk_costs_sets_hint")}</p>
                    </div>
                    <Button data-testid={K.upload} size="sm" onClick={() => setUploadOpen(true)} className="h-9 gap-1.5 text-xs font-semibold">
                        <Upload className="h-4 w-4" />{t("stk_costs_upload")}
                    </Button>
                </header>
                {failed ? (
                    <div data-testid={OPS.sc.offlineError}
                         className="mt-2 flex items-center justify-between gap-2 rounded-[var(--radius-sm)] bg-[color:rgba(220,38,38,0.08)] px-3 py-2">
                        <span className="flex items-center gap-1.5 text-xs font-semibold text-[color:var(--danger)]">
                            <WifiOff className="h-3.5 w-3.5" />{t("stk_admin_offline_error")}
                        </span>
                        <Button size="sm" variant="outline" data-testid={OPS.sc.retry} onClick={reload}>{t("stk_admin_retry")}</Button>
                    </div>
                ) : sets === null ? <InlineLoader /> : sets.length ? (
                    <ul data-testid={K.sets} className="divide-y divide-[color:var(--border-soft)]">
                        {sets.map((s) => <SetRow key={s.id} s={s} onDeactivate={setConfirmSet} />)}
                    </ul>
                ) : (
                    <p data-testid={K.sets} className="flex flex-col items-center gap-2 py-8 text-sm text-[color:var(--ink-muted)]">
                        <Inbox className="h-6 w-6 text-[color:var(--ink-faint)]" />{t("stk_costs_sets_empty")}
                    </p>
                )}
            </section>

            <section className="space-y-3 rounded-[var(--radius-lg)] border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-3">
                <header className="flex flex-wrap items-center justify-between gap-2">
                    <div className="min-w-0">
                        <h3 className="text-sm font-bold text-[color:var(--ink)]">{t("stk_costs_coverage_title")}</h3>
                        <p className="text-[11px] text-[color:var(--ink-muted)]">{t("stk_costs_coverage_hint")}</p>
                    </div>
                    <Select value={period} onValueChange={setPeriod}>
                        <SelectTrigger data-testid={K.period} className="h-10 w-full sm:w-48">
                            <CalendarDays className="h-4 w-4 text-[color:var(--brand-brown)]" />
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {periods.map((p) => <SelectItem key={p} value={p}>{fmtPeriod(p, lang)}</SelectItem>)}
                        </SelectContent>
                    </Select>
                </header>
                {covLoading && !coverage ? <InlineLoader /> : (
                    <div className={`transition-opacity duration-200 ${covLoading ? "opacity-60" : ""}`}>
                        <CoverageView coverage={coverage} bases={scope?.labels?.bases} onOpenItem={openItem} testId={K.missing} />
                    </div>
                )}
            </section>

            <CostUploadDialog open={uploadOpen} onOpenChange={setUploadOpen} scope={scope} onCommitted={reload} />

            <AlertDialog open={!!confirmSet} onOpenChange={(o) => { if (!o && !busy) setConfirmSet(null); }}>
                <AlertDialogContent className="bg-[color:var(--bg-surface)]">
                    <AlertDialogHeader className="text-start sm:text-start">
                        <AlertDialogTitle>{t("stk_costs_deactivate_title")}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {confirmSet ? fmt(t("stk_costs_deactivate_body"), { period: fmtPeriod(confirmSet.period, lang) }) : ""}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel data-testid={OPS.sc.costs.deactivateCancel} disabled={busy}>{t("stk_admin_cancel")}</AlertDialogCancel>
                        <AlertDialogAction data-testid={OPS.sc.costs.deactivateConfirm} disabled={busy}
                                           onClick={(e) => { e.preventDefault(); deactivate(); }}
                                           className="bg-[color:var(--danger)] text-white hover:opacity-90">
                            {busy ? t("stk_admin_saving") : t("stk_costs_deactivate")}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>

            {editing ? (
                <ItemDialog item={editing} scope={scope} groups={orderedGroups(scope, items)}
                            onClose={() => setEditing(null)}
                            onSaved={() => { setEditing(null); loadItems(); reload(); }} />
            ) : null}
        </div>
    );
}
